// src/pages/reports/EnhancedReports.jsx
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { BarChart3, FileText, Calendar, Filter, RefreshCw } from 'lucide-react'
import ReportsDashboard from '../../components/reports/ReportsDashboard'
import { EnhancedReportCard } from '../../components/reports/EnhancedReportCard'
import reportsService from '../../services/reportsService'

const REPORT_CATEGORIES = {
  'Financial': {
    icon: '💰',
    reports: ['sales-summary', 'payments', 'daily-sales'],
    color: 'bg-green-50 border-green-200 text-green-800'
  },
  'Inventory': {
    icon: '📦',
    reports: ['product-summary', 'stock-alerts'],
    color: 'bg-blue-50 border-blue-200 text-blue-800'
  },
  'Customer': {
    icon: '👥',
    reports: ['customers'],
    color: 'bg-purple-50 border-purple-200 text-purple-800'
  },
  'Operations': {
    icon: '⚙️',
    reports: ['employees'],
    color: 'bg-orange-50 border-orange-200 text-orange-800'
  }
}

const REPORT_CONFIGS = {
  'sales-summary': {
    title: '💰 Sales Summary',
    description: 'Overview of sales performance and revenue',
    columns: [
      { key: 'date', header: 'Date' },
      { key: 'orderNo', header: 'Order #' },
      { key: 'customer', header: 'Customer' },
      { key: 'totalBill', header: 'Amount' },
      { key: 'paymentMethod', header: 'Payment' }
    ]
  },
  'payments': {
    title: '💳 Payment Methods',
    description: 'Breakdown of payment methods used',
    columns: [
      { key: 'paymentMethod', header: 'Method' },
      { key: 'transactionCount', header: 'Transactions' },
      { key: 'totalAmount', header: 'Amount' },
      { key: 'percentage', header: 'Percentage' }
    ]
  },
  'product-summary': {
    title: '📦 Product Performance',
    description: 'Product inventory and stock levels',
    columns: [
      { key: 'productName', header: 'Product' },
      { key: 'category', header: 'Category' },
      { key: 'stockLevel', header: 'Stock' },
      { key: 'status', header: 'Status' }
    ]
  },
  'stock-alerts': {
    title: '⚠️ Stock Alerts',
    description: 'Low stock and out of stock products',
    columns: [
      { key: 'productName', header: 'Product' },
      { key: 'currentStock', header: 'Current Stock' },
      { key: 'minStock', header: 'Min Stock' },
      { key: 'status', header: 'Status' }
    ]
  },
  'customers': {
    title: '👥 Customer Report',
    description: 'Customer analytics and insights',
    columns: [
      { key: 'customerName', header: 'Name' },
      { key: 'email', header: 'Email' },
      { key: 'phone', header: 'Phone' },
      { key: 'loyaltyPoints', header: 'Points' }
    ]
  },
  'employees': {
    title: '👨‍💼 Employee Report',
    description: 'Employee information and statistics',
    columns: [
      { key: 'employeeName', header: 'Name' },
      { key: 'position', header: 'Position' },
      { key: 'department', header: 'Department' },
      { key: 'status', header: 'Status' }
    ]
  }
}

const EnhancedReports = () => {
  const [currentView, setCurrentView] = useState('dashboard') // dashboard, reports, category
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedReports, setSelectedReports] = useState(['sales-summary', 'payments'])
  const [globalFilters, setGlobalFilters] = useState({
    startDate: '',
    endDate: '',
    searchTerm: ''
  })
  const [reportData, setReportData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [lastRefresh, setLastRefresh] = useState(new Date())

  useEffect(() => {
    if (currentView === 'reports') {
      loadReportsData()
    }
  }, [currentView, selectedReports, globalFilters])

  const loadReportsData = async () => {
    if (selectedReports.length === 0) return

    setIsLoading(true)
    try {
      const dataPromises = selectedReports.map(async (reportType) => {
        const data = await reportsService.generateReport(reportType, globalFilters)
        return { reportType, data }
      })

      const results = await Promise.all(dataPromises)
      const newReportData = {}
      
      results.forEach(({ reportType, data }) => {
        newReportData[reportType] = data
      })

      setReportData(newReportData)
      setLastRefresh(new Date())
    } catch (error) {
      console.error('Error loading reports data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCategorySelect = (category) => {
    setSelectedCategory(category)
    setSelectedReports(REPORT_CATEGORIES[category].reports)
    setCurrentView('reports')
  }

  const handleRefresh = () => {
    loadReportsData()
  }

  const handleExport = (data, title, format) => {
    if (format === 'csv') {
      reportsService.exportToCSV(data, title.replace(/[^a-zA-Z0-9]/g, '_'))
    } else if (format === 'json') {
      reportsService.exportToJSON(data, title.replace(/[^a-zA-Z0-9]/g, '_'))
    } else {
      console.log(`Export to ${format} not yet implemented`)
    }
  }

  const renderNavigation = () => (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center space-x-4">
        <Button
          variant={currentView === 'dashboard' ? 'default' : 'outline'}
          onClick={() => setCurrentView('dashboard')}
          className="flex items-center space-x-2"
        >
          <BarChart3 className="h-4 w-4" />
          <span>Dashboard</span>
        </Button>
        <Button
          variant={currentView === 'reports' ? 'default' : 'outline'}
          onClick={() => setCurrentView('reports')}
          className="flex items-center space-x-2"
        >
          <FileText className="h-4 w-4" />
          <span>Reports</span>
        </Button>
      </div>

      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <Calendar className="h-4 w-4" />
        <span>Last updated: {lastRefresh.toLocaleTimeString()}</span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center space-x-1"
        >
          <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>
    </div>
  )

  const renderCategorySelection = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {Object.entries(REPORT_CATEGORIES).map(([category, config]) => (
        <Card 
          key={category} 
          className={`cursor-pointer hover:shadow-lg transition-all duration-200 ${
            selectedCategory === category ? 'ring-2 ring-blue-500' : ''
          }`}
          onClick={() => handleCategorySelect(category)}
        >
          <CardContent className="p-6 text-center">
            <div className="text-3xl mb-2">{config.icon}</div>
            <h3 className="font-semibold text-lg mb-2">{category}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {config.reports.length} report{config.reports.length !== 1 ? 's' : ''} available
            </p>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
              Click to view reports
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )

  const renderGlobalFilters = () => (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Filter className="h-5 w-5" />
          <span>Global Filters</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={globalFilters.startDate}
              onChange={(e) => setGlobalFilters({...globalFilters, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={globalFilters.endDate}
              onChange={(e) => setGlobalFilters({...globalFilters, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Term
            </label>
            <input
              type="text"
              placeholder="Search across all reports..."
              value={globalFilters.searchTerm}
              onChange={(e) => setGlobalFilters({...globalFilters, searchTerm: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📊 Reports & Analytics</h1>
          <p className="text-gray-600 mt-2">
            Comprehensive insights into your business performance
          </p>
        </div>
      </div>

      {/* Navigation */}
      {renderNavigation()}

      {/* Dashboard View */}
      {currentView === 'dashboard' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Overview</h2>
            <p className="text-gray-600">Key metrics and insights at a glance</p>
          </div>
          <ReportsDashboard />
        </div>
      )}

      {/* Reports View */}
      {currentView === 'reports' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Detailed Reports</h2>
            <p className="text-gray-600">Select a category or view individual reports</p>
          </div>

          {/* Category Selection */}
          {renderCategorySelection()}

          {/* Global Filters */}
          {selectedReports.length > 0 && renderGlobalFilters()}

          {/* Selected Category Info */}
          {selectedCategory && (
            <div className="mb-6">
              <div className={`inline-flex items-center px-4 py-2 rounded-lg ${REPORT_CATEGORIES[selectedCategory].color}`}>
                <span className="mr-2">{REPORT_CATEGORIES[selectedCategory].icon}</span>
                <span className="font-medium">{selectedCategory} Reports</span>
              </div>
            </div>
          )}

          {/* Reports Grid */}
          {selectedReports.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {selectedReports.map((reportType) => {
                const config = REPORT_CONFIGS[reportType]
                
                return (
                  <EnhancedReportCard
                    key={reportType}
                    title={config.title}
                    data={null} // Let each card load its own data
                    columns={config.columns}
                    summary={null} // Let each card load its own summary
                    filters={{}}
                    onFilterChange={() => {}} // Cards handle their own filtering now
                    onExport={handleExport}
                    loading={false} // Cards manage their own loading
                    reportType={reportType}
                  />
                )
              })}
            </div>
          )}

          {/* No Reports Selected */}
          {selectedReports.length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Reports Selected</h3>
              <p className="text-gray-600">Choose a category above to view reports</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EnhancedReports